import { nanoid } from "nanoid";
import {useEffect} from "react";
import moment from "moment-timezone";

export const projectTitle = "GoCicero"
export const APIurl = /*!process.env.NODE_ENV || process.env.NODE_ENV === 'development' ? 'http://localhost:8808' :*/ 'https://api.gocicero.it'
export const QrPrefix = "https://qr.gocicero.it/c/"

export const ExperienceTags = [
    // Età
    "Bambini",
    "Adolescenti",
    "Adulti",
    "Over 65",
    "Famiglie",
    "Tutte le età",

    // Giorni
    "Giorni feriali",
    "Giorni festivi",
    "Weekend",
    "Durante la settimana",
    "Solo domenica",

    // Orario
    "Mattina",
    "Pomeriggio",
    "Sera",
    "Notturna",
    "All'alba",

    // Meteo
    "Pioggia",
    "Sole",
    "Indipendente dal meteo",
    "All'aperto",
    "Al chiuso",

    // Durata
    "Breve (meno di 1h)",
    "Media (1-2h)",
    "Lunga (oltre 2h)",

    // Difficoltà
    "Facile",
    "Media difficoltà",
    "Difficile",
    "Esperti",

    // Interattività
    "Osservativa",
    "Interattiva",
    "Collaborativa",
    "Competitiva",
    "Immersiva",

    // Accessibilità
    "Accessibile ai disabili",
    "Non accessibile ai disabili",
    "Adatta a non udenti",
    "Adatta a non vedenti",

    // Tema
    "Storia",
    "Arte",
    "Scienza",
    "Mistero",
    "Fantasy",
    "Giallo",
    "Avventura",
    "Tecnologia",
    "Sportiva",
    "Musicale",
    "Cucina",
    "Natura",
    "Sostenibilità",
    "Animali",
    "Spazio",
    "Escape Room",
    "Orientamento",
    "Laboratorio creativo",
    "Storytelling",
    "Virtuale",
    "Realtà aumentata",

    // Tipo di esperienza
    "Tour guidato",
    "Gioco di ruolo",
    "Caccia al tesoro",
    "Gioco urbano",
    "Esperienza digitale",
    "Workshop",
    "Didattica",
    "Evento unico",
    "Esperienza permanente",
    "Esperienza stagionale",

    // Lingua
    "Italiano",
    "Inglese",
    "Multilingua",

    // Target
    "Turismo",
    "Scuole",
    "Team building",
    "Eventi aziendali",
    "Compleanni",
    "Addii al celibato/nubilato",

    // Altro
    "Richiede prenotazione",
    "Accesso libero",
    "Gratuito",
    "Pagamento anticipato",
    "Richiede dispositivi propri",
].sort();


declare global {
    interface String {
        isEmpty(): boolean;
    }
}

String.prototype.isEmpty = function (): boolean {
    return this.trim().length === 0;
};

export function useUnloadWarning() {
    useEffect(() => {
        const onBeforeUnload = (ev: BeforeUnloadEvent) => {

            ev.returnValue = "Anything you wanna put here!";
            return "Anything here as well, doesn't matter!";
        };

        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, []);
}

export function formatAsLink(input: string): string {
    return encodeURIComponent(input.trim());
}

export function getUUID(): string {
    return nanoid()
}

export function isValidEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email.trim());
}

/** IANA timezone of the admin's browser, e.g. "Europe/Rome". */
export function getUserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Formats a UTC ISO datetime string for display in the admin's local timezone, with a label. */
export function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return "";
    const m = moment(iso);
    if (!m.isValid()) return "";
    return `${m.format("DD/MM/YYYY HH:mm")} (${getUserTimezone()})`;
}

/** Same as formatDateTime but without the trailing timezone label — use when
 * combining multiple dates in one line and appending a single shared label. */
export function formatDateTimeShort(iso: string | null | undefined): string {
    if (!iso) return "";
    const m = moment(iso);
    return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "";
}

/** Formats a UTC ISO datetime string as a date only (no time/timezone label — used for calendar-date fields like birthdate). */
export function formatDateOnly(iso: string | null | undefined): string {
    if (!iso) return "";
    const m = moment(iso);
    return m.isValid() ? m.format("DD/MM/YYYY") : "";
}

/** Formats a euro amount with proper decimals/thousands separators (`1.234,50 €`) —
 * existing price displays elsewhere in this codebase just do `${value}€`, no formatting. */
export function formatCurrency(amount: number | null | undefined): string {
    if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";
    return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(amount);
}

/** Populates an `<input type="datetime-local">` from a UTC ISO string, in the browser's local time. */
export function toDatetimeLocalInput(iso: string | null | undefined): string {
    if (!iso) return "";
    const m = moment(iso);
    return m.isValid() ? m.format("YYYY-MM-DDTHH:mm") : "";
}

/** Converts a naive `<input type="datetime-local">` value (browser-local time) to a UTC ISO string for the backend. */
export function fromDatetimeLocalInput(value: string): string | null {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m.toISOString() : null;
}